import React, { useEffect, useState, Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import clsx from 'clsx';
import { Location } from 'history';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { getMenuItemsGrouped, getAllAppRoutes } from 'routes';
import useStore from 'store';
import { RouteConfig, RouteId } from 'types';
import AvatarNavigationItem from './AvatarNavigationItem';
import ClickableNavigationItem from './ClickableNavigationItem';
import LogoutFrame from '../../LogoutFrame';
import PINNavigationItem from './PINNavigationItem';
import TabSkipButton from './TabSkipButton';
import ToggleButton from './ToggleButton';
import PLILogo from '../../../assets/PLI_Logo.svg';
import { PliUserSetting } from 'store/types';

interface NavigationDrawerProps extends RouteComponentProps {
  toggleDrawer: () => void;
  isDrawerOpen: boolean;
  desktopView?: boolean;
}

const routes = getAllAppRoutes();

const findSelectedNavItem = (routes: RouteConfig[], location: Location): RouteId => {
  const menuItemMatch = routes.find((x: RouteConfig) => location.pathname.startsWith(x.path));

  return menuItemMatch?.id || RouteId.DASHBOARD;
};

const NavigationDrawer = (props: NavigationDrawerProps): React.ReactElement => {
  const { history, location, toggleDrawer, isDrawerOpen: isOpen } = props;
  const [{ ui, userPrivilegesCache, user }, { setNavigationDrawer }] = useStore();
  const selectedNavItemRouteId = findSelectedNavItem(routes, location);
  const menuItemsGrouped = getMenuItemsGrouped(userPrivilegesCache);
  const leftNavigationSetting = useMemo(
    () => user.me?.settings?.find(x => x.setting === PliUserSetting.LEFT_NAVIGATION)?.value?.open,
    [user.me]
  );

  const classes = useStyles();
  const { t } = useTranslation('NavigationDrawer');
  const drawerVariant = ui.isDesktop ? 'permanent' : 'temporary';
  const [logout, setLogout] = useState<boolean>(false);

  useEffect(() => {
    return (): void => setLogout(false);
  }, []);

  const handleClick = (path: string) => (): void => {
    if (path === '/logout') {
      setLogout(true);
      return;
    }

    if (!ui.isDesktop) {
      toggleDrawer();
    }
    if (path) {
      history.push(path);
    }
  };
  // sets drawer state from user settings
  useEffect(() => {
    if (!user.me?.settings) return;
    setNavigationDrawer(leftNavigationSetting);
    // eslint-disable-next-line
  }, []);

  return (
    <Drawer
      open={isOpen}
      variant={drawerVariant}
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: isOpen,
        [classes.drawerClose]: !isOpen
      })}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: isOpen,
          [classes.drawerClose]: !isOpen
        })
      }}
      PaperProps={{ className: classes.paper }}
    >
      <TabSkipButton open={isOpen} />
      <div
        className={clsx({
          [classes.drawerContainerOpen]: isOpen,
          [classes.drawerContainerClose]: !isOpen
        })}
      >
        <div className={clsx(classes.topHolder, { [classes.topHolderClose]: !isOpen })}>
          <ToggleButton isDesktop={ui.isDesktop} onClick={toggleDrawer} open={isOpen} />
          <img
            src={PLILogo}
            alt="Live Insights - The Dashboard for Digital Teaching"
            className={clsx(classes.logo, { [classes.logoClose]: !isOpen })}
            onClick={handleClick('/')}
          />
        </div>
        <Divider className={classes.divider} />
        <PINNavigationItem open={isOpen} />
        <AvatarNavigationItem
          open={isOpen}
          selected={Boolean(location.pathname.includes('/profile'))}
          onClick={handleClick('/profile')}
        />
        {menuItemsGrouped.map((routeSet: RouteConfig[], index) => {
          return (
            <Fragment key={`route-group-${index}`}>
              <Divider className={classes.divider} key={`nav-subdivider-${index}`} />
              {routeSet.map(({ getTitle, MenuIcon, path, id }) => {
                return (
                  <ClickableNavigationItem
                    selected={selectedNavItemRouteId === id} //isNavItemSelected(path, location)
                    key={path}
                    label={t(getTitle())}
                    icon={<MenuIcon />}
                    open={isOpen}
                    onClick={handleClick(path)}
                  />
                );
              })}
            </Fragment>
          );
        })}
        {logout && <LogoutFrame />}
      </div>
    </Drawer>
  );
};

const useStyles = makeStyles((theme: Theme) => {
  const MENU_WIDTH = 270;

  return {
    '@media print': {
      drawer: { display: 'none' }
    },
    divider: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      [theme.breakpoints.up('sm')]: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
      }
    },
    paper: {
      position: 'unset',
      minHeight: '100vh'
    },
    drawer: {
      overflowX: 'hidden',
      overflowY: 'auto',
      width: MENU_WIDTH,
      flexShrink: 0,
      whiteSpace: 'nowrap',
      boxShadow: '2px 0 6px #b3b3b3',
      zIndex: theme.zIndex.drawer,
      '&::-webkit-scrollbar': {
        backgroundColor: 'transparent',
        display: 'none'
      },
      '&:hover::-webkit-scrollbar-thumb': {
        borderRadius: 16,
        backgroundColor: 'rgba(117, 117, 117, 0.5)',
        backgroundClip: 'content-box',
        border: '5px solid transparent'
      }
    },
    drawerOpen: {
      width: MENU_WIDTH,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen
      })
    },
    drawerClose: {
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
      }),
      overflowX: 'hidden',
      width: theme.spacing(7),
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(7)
      }
    },
    drawerContainerOpen: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingTop: theme.spacing(0.6),
      paddingBottom: theme.spacing(0.6),
      transition: theme.transitions.create('padding', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen
      }),
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(2)
      }
    },
    drawerContainerClose: {
      paddingTop: theme.spacing(2),
      transition: theme.transitions.create('padding', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
      })
    },
    logo: {
      height: 38,
      cursor: 'pointer',
      transition: theme.transitions.create('all', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
      }),
      [theme.breakpoints.up('sm')]: {
        height: 44
      }
    },
    logoClose: {
      marginLeft: 7,
      cursor: 'pointer',
      width: 196,
      transition: theme.transitions.create('all', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
      })
    },
    topHolder: {
      justifyContent: 'space-between',
      alignItems: 'center',
      display: 'flex'
    },
    topHolderClose: {
      [theme.breakpoints.up('sm')]: {
        width: '50px',
        overflow: 'hidden'
      }
    }
  };
});

export default withRouter(NavigationDrawer);
